import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { apiRequest } from '../authConfig';

/**
 * Creates an Axios instance with MSAL authentication interceptor
 * @param msalInstance MSAL IPublicClientApplication instance
 * @returns Configured Axios instance
 */
export function createApiClient(msalInstance: IPublicClientApplication): AxiosInstance {
  const apiClient = axios.create({
    baseURL: '/api', // Proxied by Vite to https://localhost:7145/v1
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add Bearer token
  apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        try {
          // Acquire token silently
          const response = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          });

          console.log('[DEBUG] Token acquired successfully');
          console.log('[DEBUG] Scopes requested:', apiRequest.scopes);
          console.log('[DEBUG] Scopes in token:', response.scopes);
          console.log('[DEBUG] Token (first 50 chars):', response.accessToken.substring(0, 50));

          // Add Authorization header
          if (config.headers) {
            config.headers.Authorization = `Bearer ${response.accessToken}`;
          }
        } catch (error) {
          console.error('Failed to acquire token:', error);

          // If silent token acquisition fails, try interactive
          try {
            const response = await msalInstance.acquireTokenPopup(apiRequest);
            if (config.headers) {
              config.headers.Authorization = `Bearer ${response.accessToken}`;
            }
          } catch (interactiveError) {
            console.error('Failed to acquire token interactively:', interactiveError);
          }
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('Unauthorized - token may be expired');
        // Optionally trigger re-authentication
      }
      return Promise.reject(error);
    }
  );

  return apiClient;
}
